import axios from 'axios';
import { LoginCredentials, RegisterData, DashboardResponse, Child, Product, Invitation, Representative, Activity } from '../types';

// Usar la URL del entorno o fallback a localhost
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

console.log('API URL configurada:', API_URL);

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Interceptor para agregar el token a las peticiones
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    console.log('🔍 Interceptor: Token encontrado:', token ? 'SÍ' : 'NO');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log('🔍 Interceptor: Headers configurados:', config.headers);
    }
    return config;
});

// Interceptor para manejar refresh tokens automáticamente
// TEMPORALMENTE DESHABILITADO PARA DEBUGGING
/*
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        
        // Solo intentar refresh si es un error 401 y no hemos intentado ya
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest._isRefreshRequest) {
            originalRequest._retry = true;
            
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
                try {
                    const response = await authService.refreshToken(refreshToken);
                    localStorage.setItem('token', response.access_token);
                    
                    // Reintentar la petición original con el nuevo token
                    originalRequest.headers.Authorization = `Bearer ${response.access_token}`;
                    return api(originalRequest);
                } catch (refreshError) {
                    // Si el refresh token también expiró, hacer logout
                    console.log('Refresh token expirado, haciendo logout');
                    await authService.logout(refreshToken);
                    window.location.href = '/login';
                    return Promise.reject(refreshError);
                }
            } else {
                // No hay refresh token, redirigir al login
                console.log('No hay refresh token, redirigiendo al login');
                window.location.href = '/login';
            }
        }
        
        return Promise.reject(error);
    }
);
*/

export const authService = {
    login: async (credentials: LoginCredentials) => {
        console.log('🔐 Intentando login con:', credentials.username);
        console.log('🌐 URL de la API:', API_URL);
        
        try {
            const formData = new FormData();
            formData.append('username', credentials.username);
            formData.append('password', credentials.password);
            
            console.log('📤 Enviando petición de login...');
            const response = await axios.post(`${API_URL}/representatives/token`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            console.log('✅ Login exitoso:', response.data);
            console.log('🔍 Estructura de respuesta:', {
                hasAccessToken: !!response.data.access_token,
                hasRefreshToken: !!response.data.refresh_token,
                hasUser: !!response.data.user,
                accessTokenLength: response.data.access_token?.length || 0,
                refreshTokenLength: response.data.refresh_token?.length || 0
            });
            
            // Guardar usuario autenticado en localStorage
            if (response.data.user) {
                localStorage.setItem('authUser', JSON.stringify(response.data.user));
                console.log('✅ Usuario guardado en localStorage');
            }
            // Guardar el token para autenticación
            if (response.data.access_token) {
                localStorage.setItem('token', response.data.access_token);
                console.log('✅ Access token guardado en localStorage');
            }
            // Guardar el refresh token para renovación automática
            if (response.data.refresh_token) {
                localStorage.setItem('refreshToken', response.data.refresh_token);
                console.log('✅ Refresh token guardado en localStorage');
            } else {
                console.error('❌ No se encontró refresh_token en la respuesta');
            }
            return response.data;
        } catch (error: any) {
            console.error('❌ Error en login:', error);
            console.error('📋 Detalles del error:', error.response?.data);
            throw error;
        }
    },

    changePassword: async (passwordData: { current_password: string; new_password: string }) => {
        const response = await api.post('/representatives/change-password', passwordData);
        return response.data;
    },

    requestPasswordReset: async (email: string) => {
        const formData = new FormData();
        formData.append('email', email);
        
        const response = await axios.post(`${API_URL}/representatives/reset-password-request`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    confirmPasswordReset: async (resetData: { email: string; reset_code: string; new_password: string }) => {
        const formData = new FormData();
        formData.append('email', resetData.email);
        formData.append('reset_code', resetData.reset_code);
        formData.append('new_password', resetData.new_password);
        
        const response = await axios.post(`${API_URL}/representatives/reset-password-confirm`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    refreshToken: async (refreshToken: string) => {
        const response = await api.post('/representatives/refresh', {
            refresh_token: refreshToken
        });
        return response.data;
    },

    logout: async (refreshToken: string) => {
        try {
            await api.post('/representatives/logout', {
                refresh_token: refreshToken
            });
        } catch (error) {
            console.error('Error en logout:', error);
        } finally {
            // Limpiar tokens del localStorage
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('userId');
            localStorage.removeItem('dashboardData');
        }
    },

    register: async (data: RegisterData) => {
        console.log('📝 Intentando registro con:', data.email);
        console.log('🌐 URL de la API:', API_URL);
        
        try {
            console.log('📤 Enviando petición de registro...');
            
            // Crear FormData para enviar los datos como el backend espera
            const formData = new FormData();
            formData.append('full_name', data.full_name);
            formData.append('email', data.email);
            formData.append('password', data.password);
            
            // Solo agregar campos opcionales si tienen valores válidos
            if (data.country && data.country.trim() !== '') {
                formData.append('country', data.country);
            }
            if (data.phone && data.phone.trim() !== '') {
                formData.append('phone', data.phone);
            }
            if (data.birth_date && data.birth_date.trim() !== '') {
                formData.append('birth_date', data.birth_date);
            }
            if (data.referral_code && data.referral_code.trim() !== '') {
                formData.append('referral_code', data.referral_code);
            }
            
            // Log de los datos que se están enviando
            console.log('📋 Datos del FormData:');
            console.log(`   full_name: ${data.full_name}`);
            console.log(`   email: ${data.email}`);
            console.log(`   password: [HIDDEN]`);
            console.log(`   country: ${data.country && data.country.trim() !== '' ? data.country : 'No enviado'}`);
            console.log(`   phone: ${data.phone && data.phone.trim() !== '' ? data.phone : 'No enviado'}`);
            console.log(`   birth_date: ${data.birth_date && data.birth_date.trim() !== '' ? data.birth_date : 'No enviado'}`);
            console.log(`   referral_code: ${data.referral_code && data.referral_code.trim() !== '' ? data.referral_code : 'No enviado'}`);
            
            const response = await axios.post(`${API_URL}/representatives/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            console.log('✅ Registro exitoso:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('❌ Error en registro:', error);
            console.error('📋 Detalles del error:', error.response?.data);
            console.error('📋 Status code:', error.response?.status);
            console.error('📋 Headers:', error.response?.headers);
            
            // Si es un error 422, mostrar más detalles
            if (error.response?.status === 422) {
                console.error('📋 Error 422 - Detalles de validación:', error.response.data);
                if (error.response.data?.detail) {
                    console.error('📋 Detalles específicos:', error.response.data.detail);
                }
            }
            
            throw error;
        }
    },
};

export const dashboardService = {
    getDashboard: async (): Promise<DashboardResponse> => {
        try {
            console.log('🔍 dashboardService.getDashboard: Iniciando llamada a /representatives/me');
            const response = await api.get('/representatives/me');
            console.log('🔍 dashboardService.getDashboard: Respuesta completa:', response.data);
            
            // Validar que la respuesta tenga la estructura esperada
            if (!response.data || !response.data.data) {
                console.error('❌ dashboardService.getDashboard: Estructura de respuesta inválida:', response.data);
                throw new Error('Estructura de respuesta inválida del servidor');
            }
            
            // El endpoint /me retorna: { success: true, data: { representative: {...}, children: [...], products: [...], invitations: [...] } }
            // Necesitamos extraer el data del response
            return response.data.data;
        } catch (error) {
            console.error('❌ dashboardService.getDashboard: Error en la llamada:', error);
            throw error;
        }
    },
};

export const userService = {
    getCurrentUser: async (): Promise<any> => {
        try {
            console.log('🔍 userService.getCurrentUser: Iniciando llamada a /representatives/me');
            const response = await api.get('/representatives/me');
            console.log('🔍 userService.getCurrentUser: Respuesta completa de axios:', response);
            console.log('🔍 userService.getCurrentUser: response.data:', response.data);
            
            // Validar que la respuesta tenga la estructura esperada
            if (!response.data || !response.data.data) {
                console.error('❌ userService.getCurrentUser: Estructura de respuesta inválida:', response.data);
                throw new Error('Estructura de respuesta inválida del servidor');
            }
            
            // El backend retorna: { success: true, data: { representative: {...}, children: [...], products: [...], invitations: [...] } }
            // Necesitamos extraer solo los datos del representative y agregar referral_stats
            const representativeData = response.data.data.representative;
            
            if (!representativeData) {
                console.error('❌ userService.getCurrentUser: No se encontraron datos del representante:', response.data.data);
                throw new Error('No se encontraron datos del representante');
            }
            
            const result = {
                ...representativeData,
                referral_stats: response.data.data.referral_stats || {
                    has_referral_code: false,
                    code: null,
                    total_referrals: 0,
                    referral_link: null
                }
            };
            
            console.log('🔍 userService.getCurrentUser: Retornando:', result);
            return result;
        } catch (error) {
            console.error('❌ userService.getCurrentUser: Error en la llamada:', error);
            throw error;
        }
    },
    
    updateUser: async (userData: Partial<Representative>): Promise<any> => {
        const formData = new FormData();
        
        // Solo agregar campos que tienen valores
        if (userData.full_name) formData.append('full_name', userData.full_name);
        if (userData.country) formData.append('country', userData.country);
        if (userData.phone) formData.append('phone', userData.phone);
        if (userData.birth_date) formData.append('birth_date', userData.birth_date);
        
        const response = await api.put('/representatives/me', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    // 🗑️ ELIMINADO: Los códigos de referido se generan automáticamente

    getReferralStats: async (): Promise<any> => {
        const response = await api.get('/representatives/referral-stats');
        return response.data;
    },

    getStarSummary: async (): Promise<any> => {
        const response = await api.get('/representatives/star-summary');
        return response.data;
    },
};

export const childrenService = {
    getChildren: async (): Promise<Child[]> => {
        const response = await api.get('/children/');
        return response.data;
    },

    getChild: async (id: number): Promise<Child> => {
        const response = await api.get(`/children/${id}`);
        return response.data;
    },

    createChild: async (child: Omit<Child, 'id' | 'representative_id'>): Promise<Child> => {
        const response = await api.post('/children/', child);
        return response.data;
    },

    updateChild: async (id: number, child: Partial<Child>): Promise<Child> => {
        const response = await api.put(`/children/${id}`, child);
        return response.data;
    },

    deleteChild: async (id: number): Promise<void> => {
        await api.delete(`/children/${id}`);
    },
};

export const productsService = {
    getProducts: async (): Promise<Product[]> => {
        const response = await api.get('/products/');
        return response.data;
    },

    createProduct: async (product: Omit<Product, 'id' | 'representative_id'>): Promise<Product> => {
        const response = await api.post('/products/', product);
        return response.data;
    },

    updateProduct: async (id: number, product: Partial<Product>): Promise<Product> => {
        const response = await api.put(`/products/${id}`, product);
        return response.data;
    },

    deleteProduct: async (id: number): Promise<void> => {
        await api.delete(`/products/${id}`);
    },
};

export const invitationsService = {
    getInvitations: async (): Promise<Invitation[]> => {
        const response = await api.get('/invites/');
        return response.data;
    },

    createInvitation: async (): Promise<Invitation> => {
        const response = await api.post('/invites/');
        return response.data;
    },

    validateInvitation: async (code: string): Promise<Invitation> => {
        const response = await api.post(`/invites/validate/${code}`);
        return response.data;
    },

    useInvitation: async (code: string): Promise<Invitation> => {
        const response = await api.post(`/invites/use/${code}`);
        return response.data;
    },

    deleteInvitation: async (code: string): Promise<void> => {
        await api.delete(`/invites/${code}`);
    },
};

export const activityService = {
    getChildActivities: async (childId: number): Promise<Activity[]> => {
        const response = await api.get(`/activities/child/${childId}`);
        return response.data;
    },

    submitActivity: async (childId: number, pdfData: string): Promise<Activity> => {
        const response = await api.post('/activities/submit', {
            child_id: childId,
            pdf_data: pdfData
        });
        return response.data;
    },

    getActivity: async (activityId: number): Promise<Activity> => {
        const response = await api.get(`/activities/${activityId}`);
        return response.data;
    },

    // Nuevos métodos para libros y experiencias
    getBooksOverview: async (childId: number): Promise<any> => {
        const response = await api.get(`/activities/books/overview?child_id=${childId}`);
        return response.data;
    },

    validateProductCode: async (code: string, childId: number): Promise<any> => {
        const formData = new FormData();
        formData.append('code', code);
        formData.append('child_id', childId.toString());
        
        const response = await api.post('/products/validate-code', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    getBookActivities: async (bookNumber: number, childId: number): Promise<any> => {
        const response = await api.get(`/activities/books/${bookNumber}/activities?child_id=${childId}`);
        return response.data;
    },

    uploadExperience: async (formData: FormData): Promise<any> => {
        const response = await api.post('/activities/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    downloadDocumentation: async (bookNumber: number, activityNumber: number): Promise<void> => {
        try {
            const response = await api.get(`/activities/download-documentation/${bookNumber}/${activityNumber}`, {
                responseType: 'blob',
                validateStatus: (status) => status >= 200 && status < 400 // aceptar redirecciones
            });

            // Si es una redirección (307), abrir la URL de S3
            if (response.request?.responseURL && response.request.responseURL.includes('s3.amazonaws.com')) {
                window.open(response.request.responseURL, '_blank');
                return;
            }

            // Si no es redirección, descargar el blob como antes
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const contentDisposition = response.headers['content-disposition'];
            let filename = `actividad_${activityNumber}_documentacion.pdf`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            // Si la respuesta es una redirección manual, abrir la URL
            if (error.response && error.response.status === 307 && error.response.headers['location']) {
                window.open(error.response.headers['location'], '_blank');
                return;
            }
            throw error;
        }
    },

    getExperiences: async (childId?: number, bookNumber?: number): Promise<any> => {
        let url = '/activities/experiences';
        const params = new URLSearchParams();
        if (childId) params.append('child_id', childId.toString());
        if (bookNumber) params.append('book_number', bookNumber.toString());
        if (params.toString()) url += `?${params.toString()}`;
        
        const response = await api.get(url);
        return response.data;
    },
};

export const adminService = {
    getAdminUsers: async (): Promise<import('../types').AdminUserSummary[]> => {
        const response = await api.get('/admin/users');
        return response.data;
    },

    getAdminUserDetail: async (userId: number): Promise<import('../types').AdminUserDetail> => {
        const response = await api.get(`/admin/users/${userId}`);
        return response.data;
    },

    getAdminActivities: async (): Promise<import('../types').AdminActivityDetail[]> => {
        const response = await api.get('/admin/activities');
        return response.data;
    },

    reviewActivity: async (activityId: number, reviewData: import('../types').ActivityReviewRequest): Promise<import('../types').ActivityReviewResponse> => {
        const response = await api.post(`/admin/activities/${activityId}/review`, reviewData);
        return response.data;
    },

    getAdminChildActivities: async (childId: number): Promise<import('../types').AdminActivityDetail[]> => {
        const response = await api.get(`/admin/children/${childId}/activities`);
        return response.data;
    },

    generateCodes: async (bookNumber: number, quantity: number): Promise<{ codes: import('../types').GeneratedCode[] }> => {
        const response = await api.post('/admin/generate-codes', {
            book_id: bookNumber,
            amount: quantity
        });
        return response.data;
    },

    validateAndDistributeTfiats: async (): Promise<any> => {
        const response = await api.post('/admin/validate-and-distribute-tfiats');
        return response.data;
    },

    getTfiatDistributionStatus: async (): Promise<any> => {
        const response = await api.get('/admin/tfiat-distribution-status');
        return response.data;
    },

    distributeTfiatsControlled: async (): Promise<any> => {
        const response = await api.post('/admin/distribute-tfiats-controlled');
        return response.data;
    },

    getCompanyMetrics: async (): Promise<any> => {
        const response = await api.get('/compensation/company-metrics');
        return response.data;
    },

    resetFinancialSystem: async (): Promise<any> => {
        const response = await api.post('/admin/reset-financial-system');
        return response.data;
    },

    getTfiatDistributionTable: async (): Promise<any> => {
        const response = await api.get('/admin/tfiat-distribution-table');
        return response.data;
    },

    addManualTfiats: async (data: { user_id: number; amount: number; reason: string }): Promise<any> => {
        const response = await api.post('/admin/add-manual-tfiats', data);
        return response.data;
    },

    addManualStars: async (data: { user_id: number; star_type: string; reason: string }): Promise<any> => {
        const response = await api.post('/admin/add-manual-stars', data);
        return response.data;
    },

    getTfiatDistributionSummary: async (): Promise<any> => {
        const response = await api.get('/admin/tfiat-distribution-summary');
        return response.data;
    },

    deleteManualStar: async (starId: number): Promise<any> => {
        const response = await api.delete(`/admin/delete-manual-star/${starId}`);
        return response.data;
    },

    deleteManualStarByType: async (userId: number, starType: string): Promise<any> => {
        const response = await api.delete(`/admin/delete-manual-star-by-type?user_id=${userId}&star_type=${starType}`);
        return response.data;
    },

    deleteManualTfiats: async (userId: number, amount: number): Promise<any> => {
        const response = await api.delete(`/admin/delete-manual-tfiats/${userId}?amount=${amount}`);
        return response.data;
    },

    // Funcionalidad para eliminar usuarios
    deleteUser: async (userId: number): Promise<any> => {
        const response = await api.delete(`/admin/users/${userId}`);
        return response.data;
    },

    // Funcionalidad para desactivar usuarios (soft delete)
    deactivateUser: async (userId: number): Promise<any> => {
        const response = await api.put(`/admin/users/${userId}/deactivate`);
        return response.data;
    },

    // Funcionalidad para reactivar usuarios
    activateUser: async (userId: number): Promise<any> => {
        const response = await api.put(`/admin/users/${userId}/activate`);
        return response.data;
    },
};

export const compensationService = {
    getCompensationData: async (): Promise<any> => {
        const response = await api.get('/compensation');
        return response.data;
    },

    getTransactionHistory: async (limit: number = 10): Promise<any> => {
        const response = await api.get(`/compensation/transactions?limit=${limit}`);
        return response.data;
    },

    getCompanyMetrics: async (): Promise<any> => {
        const response = await api.get('/compensation/company-metrics');
        return response.data;
    },

    simulateCompensation: async (amount: number): Promise<any> => {
        const response = await api.post('/compensation/simulate', { amount });
        return response.data;
    },
};

export const treasuryService = {
    getTreasuryData: async (): Promise<any> => {
        const response = await api.get('/admin/treasury');
        return response.data;
    },
}; 

// Waiting list / KickStarter
export const waitlistService = {
    joinKickstarter: async (email: string): Promise<any> => {
        const response = await api.post('/waitlist/kickstarter', { email });
        return response.data;
    },
    
    getWaitlist: async (): Promise<any> => {
        const response = await api.get('/waitlist/');
        return response.data;
    },
    
    getWaitlistStats: async (): Promise<any> => {
        const response = await api.get('/waitlist/stats');
        return response.data;
    },
};